/**
 * Wall tablets (kitchen iPad PWA, hallway Android app).
 *
 * A tablet is a device account: its own `users` row (username `tablet-<id>`, no password) with a normal
 * per-home membership, so the access/grant system scopes it like any user. Admins create it in Admin ›
 * Tablets, which issues a one-time pairing code (and QR link `/pair?code=…`). The tablet redeems it once
 * for a long-lived device session cookie (365 days, sliding), then lives at `/t/<tabletId>`.
 *
 * REST (cookie auth):
 *   POST /api/tablet/pair {code}                       → TabletPairResult (sets the session cookie; single use, rate-limited per IP)
 *   GET  /api/tablets/:id                              → TabletConfig (the tablet itself, or an admin of its home)
 *   POST /api/tablets/:id/keypad {code}                → KeypadResult; 429 after 5 wrong codes a minute (audited)
 *   GET  /api/homes/:home/buses                        → BusBoard (Oxontime, cached ~30 s)
 *   Admin: GET/POST /api/admin/homes/:home/tablets, PATCH/DELETE …/tablets/:id,
 *          POST …/tablets/:id/pair-code (new code), …/revoke, …/command {command: TabletCommand}
 *
 * Server → tablet commands ride the normal `/ws` socket as `{t:'tablet', command}` (TabletMsg), sent only to
 * sockets signed in as that tablet's device user.
 */
import { z } from 'zod';
export const TabletLayout = z.enum(['kitchen', 'hallway']);
export const TabletId = z.string().min(2).max(32).regex(/^[a-z0-9][a-z0-9-]*$/);
export const TabletScene = z.object({
    id: z.string().regex(/^scene\.[a-z0-9_]+$/),
    label: z.string().min(1).max(24),
    /** Big button (first row) vs small secondary. */
    primary: z.boolean().default(false),
});
/** Woodlands kitchen defaults: Day / Night / Off big, Cook / Eat / Morning small. */
export const DEFAULT_TABLET_SCENES = [
    { id: 'scene.bright', label: 'Day', primary: true },
    { id: 'scene.evening', label: 'Night', primary: true },
    { id: 'scene.dining_off', label: 'Off', primary: true },
    { id: 'scene.cook', label: 'Cook', primary: false },
    { id: 'scene.entertain', label: 'Eat', primary: false },
    { id: 'scene.morning', label: 'Morning', primary: false },
];
/**
 * The scene to highlight: activated within `windowS` (10 min) and none of its member lights changed since
 * (a 5 s grace for the scene itself applying). At most one: the most recent. `changedAt(id)` = member's last change.
 */
export function activeScene(scenes, changedAt, nowS, windowS = 600) {
    const recent = scenes.filter((s) => s.lastActivated !== null && nowS - s.lastActivated <= windowS).sort((a, b) => b.lastActivated - a.lastActivated);
    const s = recent[0];
    if (!s)
        return null;
    const moved = s.members.some((id) => { const t = changedAt(id); return t !== undefined && t > s.lastActivated + 5; });
    return moved ? null : s.id;
}
export const TabletLights = z.object({
    /** Kitchen tiles (default: the kitchen room's light groups + lights). */
    room: z.array(z.string().regex(/^[a-z_]+\.[a-z0-9_]+$/)).max(24).optional(),
    /** Outside row (default: lights in outside / outdoor rooms). */
    outside: z.array(z.string().regex(/^[a-z_]+\.[a-z0-9_]+$/)).max(12).optional(),
});
/**
 * Dark screen: once every light tile in `rooms` has been off (and the screen untouched) for `afterS`, the page goes
 * black and the backlight to minimum; a tap turns `wakeLight` on (HA scenes / automations do the rest) and wakes it.
 * Rooms are model room ids (`dining` also finds an HA area id `dining_room`). wakeLight null = a tap only wakes.
 */
export const TabletDark = z.object({
    rooms: z.array(z.string().regex(/^[a-z0-9_-]+$/)).max(8).optional(),
    wakeLight: z.string().regex(/^[a-z_]+\.[a-z0-9_]+$/).nullable().optional(),
    afterS: z.number().int().min(5).max(3600).optional(),
});
/** Woodlands: the kitchen iPad watches the kitchen + dining room, the hallway tablet the hallway. */
export const DEFAULT_TABLET_DARK = {
    kitchen: { rooms: ['kitchen', 'dining'], wakeLight: 'light.dining_room_main', afterS: 30 },
    hallway: { rooms: ['hallway'], wakeLight: 'light.hallway_ceiling', afterS: 30 },
};
/** The layout's defaults with the admin's override on top (an empty room list = the default rooms). */
export function tabletDark(layout, override) {
    const d = DEFAULT_TABLET_DARK[layout];
    return {
        rooms: override?.rooms?.length ? [...override.rooms] : [...d.rooms],
        wakeLight: override?.wakeLight !== undefined ? override.wakeLight : d.wakeLight,
        afterS: override?.afterS ?? d.afterS,
    };
}
export const CreateTabletInput = z.object({
    id: TabletId,
    name: z.string().min(1).max(60),
    layout: TabletLayout,
    idleMinutes: z.number().int().min(0).max(240).default(5),
    /** Role template for the device's membership (default: the built-in "Wall tablet"). */
    templateId: z.string().optional(),
});
export const UpdateTabletInput = z.object({
    name: z.string().min(1).max(60).optional(),
    layout: TabletLayout.optional(),
    idleMinutes: z.number().int().min(0).max(240).optional(),
    /** null clears the override. */
    lights: TabletLights.nullable().optional(),
    /** null = back to the defaults. */
    scenes: z.array(TabletScene).max(9).nullable().optional(),
    /** null = back to the layout's defaults. */
    dark: TabletDark.nullable().optional(),
});
// ---------------------------------------------------------------- command channel
export const TabletCommand = z.discriminatedUnion('cmd', [
    z.object({ cmd: z.literal('show:keypad'), reason: z.enum(['pending', 'triggered', 'manual']).optional() }),
    z.object({ cmd: z.literal('show:door') }),
    z.object({ cmd: z.literal('wake') }),
    z.object({ cmd: z.literal('home') }),
    /** Speak text (HA companion TTS payload). `stream` is the companion's media_stream (alarm_stream / alarm_stream_max / music_stream). */
    z.object({
        cmd: z.literal('speak'), text: z.string().min(1).max(2000),
        stream: z.string().max(40).optional(), priority: z.string().max(20).optional(),
    }),
    /** Ordinary HA notification → on-screen banner. */
    z.object({ cmd: z.literal('notify'), title: z.string().max(200).optional(), message: z.string().max(2000) }),
    /** HA companion `command_*` message (command_volume_level, command_screen_on, command_screen_brightness_level, command_dnd…). */
    z.object({ cmd: z.literal('ha.command'), name: z.string().regex(/^command_[a-z0-9_]+$/), data: z.record(z.string(), z.unknown()) }),
]);
/**
 * HA mobile_app notify body → tablet command (HA companion app conventions):
 *   message "TTS" + data.tts_text           → speak (data.media_stream → stream, data.priority)
 *   message "command_*"                     → ha.command {name, data}
 *   "clear_notification" / "remove_channel" / "request_location_update" → null (ignored, still a success)
 *   anything else                           → notify {title, message}
 */
export function notifyToCommand(body) {
    const message = typeof body.message === 'string' ? body.message : '';
    const data = body.data && typeof body.data === 'object' && !Array.isArray(body.data) ? body.data : {};
    if (message === 'TTS') {
        const text = typeof data['tts_text'] === 'string' ? data['tts_text'].trim() : '';
        if (!text)
            return null;
        return {
            cmd: 'speak', text: text.slice(0, 2000),
            ...(typeof data['media_stream'] === 'string' ? { stream: data['media_stream'] } : {}),
            ...(typeof data['priority'] === 'string' ? { priority: data['priority'] } : {}),
        };
    }
    if (/^command_[a-z0-9_]+$/.test(message))
        return { cmd: 'ha.command', name: message, data };
    if (['clear_notification', 'remove_channel', 'request_location_update'].includes(message))
        return null;
    if (!message)
        return null;
    return { cmd: 'notify', message: message.slice(0, 2000), ...(typeof body.title === 'string' && body.title ? { title: body.title.slice(0, 200) } : {}) };
}
/** Queue lifetime for a tablet that's offline: data.ttl (seconds) if given, capped at 60 s. */
export function notifyTtlMs(data, capMs = 60_000) {
    const ttl = data && typeof data === 'object' ? Number(data['ttl']) : NaN;
    return Number.isFinite(ttl) && ttl >= 0 ? Math.min(ttl * 1000, capMs) : capMs;
}
const hm = (s) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
/** `sensor.trains_<from>_to_<to>` (National Rail Departure Times): state = next departure, attrs = one service. */
export function parseTrainSensor(entityId, state, a) {
    if (!a)
        return null;
    const svc = (a['service'] && typeof a['service'] === 'object' ? a['service'] : null);
    const std = str(svc?.['std']) ?? (state && hm(state) !== null ? state : null);
    if (!std)
        return null;
    const etd = str(svc?.['etd']) ?? 'On time';
    const cancelled = /cancel/i.test(etd) || svc?.['isCancelled'] === true;
    const exp = hm(etd);
    const sch = hm(std);
    let delayMin = null;
    if (exp !== null && sch !== null)
        delayMin = ((exp - sch + 1440) % 1440);
    const delayed = !cancelled && (/delay/i.test(etd) || (delayMin !== null && delayMin > 0));
    const cps = Array.isArray(a['calling_points']) ? a['calling_points'] : [];
    const callingPoints = cps.map((c) => ({ name: str(c['locationName']) ?? '?', crs: str(c['crs']), scheduled: str(c['st']) ?? '', expected: str(c['et']) ?? '' }));
    const target = str(a['target_station_code']);
    const last = callingPoints.find((c) => c.crs && c.crs === target) ?? callingPoints[callingPoints.length - 1];
    const origin = svc?.['origin'];
    return {
        entityId,
        from: str(a['station_name']) ?? '?',
        to: str(a['target_station_name']) ?? str(a['destination_name']) ?? '?',
        scheduled: std,
        expected: cancelled ? 'Cancelled' : etd,
        platform: str(svc?.['platform']),
        operator: str(svc?.['operator']),
        origin: str(origin?.location?.locationName),
        cancelled,
        delayed,
        delayMin: delayMin !== null && delayMin > 0 ? delayMin : null,
        arrives: last ? (hm(last.expected) !== null ? last.expected : last.scheduled) || null : null,
        callingPoints,
        message: str(a['message']),
        updatedAt: str(a['last_refresh']),
    };
}
/** Departures grouped per route (and destination), soonest first, `per` each. */
export function busesByRoute(deps, per = 3) {
    const groups = new Map();
    const when = (d) => d.expected ?? d.scheduled;
    for (const d of [...deps].sort((x, y) => when(x) - when(y))) {
        const dest = d.towards ?? d.destination;
        const k = `${d.route}|${dest}`;
        let g = groups.get(k);
        if (!g)
            groups.set(k, (g = { route: d.route, destination: dest, departures: [] }));
        if (g.departures.length < per)
            g.departures.push(d);
    }
    return [...groups.values()].sort((a, b) => when(a.departures[0]) - when(b.departures[0]));
}
const WET = new Set(['rainy', 'pouring', 'lightning-rainy', 'snowy-rainy', 'hail']);
/**
 * "Take an umbrella" if rain is likely between now and `untilHour` local (default 19:00; after that, the next 6 h):
 * a wet condition, ≥ 50 % chance, or ≥ 0.3 mm in any hour. Returns the first wet hour.
 */
export function umbrellaAdvice(hours, nowMs, opts = {}) {
    const untilHour = opts.untilHour ?? 19;
    const hourOf = opts.hourOf ?? ((ms) => new Date(ms).getHours());
    const nowH = hourOf(nowMs);
    const horizon = nowH < untilHour ? nowMs + (untilHour - nowH) * 3_600_000 : nowMs + 6 * 3_600_000;
    let chance = null;
    for (const h of hours) {
        if (h.at + 3_600_000 <= nowMs || h.at >= horizon)
            continue;
        const p = h.precipitationProbability;
        if (p !== undefined)
            chance = Math.max(chance ?? 0, p);
        // As web lib/weather isWetHour: an explicit low chance (<30 %) overrides a wet condition.
        const wet = (p !== undefined && p >= 50) || (h.precipitation ?? 0) >= 0.3 || (!!h.condition && WET.has(h.condition) && !(p !== undefined && p < 30));
        if (wet) {
            return { take: true, from: h.at, chance };
        }
    }
    return { take: false, from: null, chance };
}
//# sourceMappingURL=tablet.js.map